import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
};

const authReducer = (state, action) => {
  console.log('🔄 Auth reducer action:', action.type, 'payload:', action.payload);
  
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      console.log('✅ LOGIN_SUCCESS - setting user and token');
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGIN_FAIL':
    case 'LOGOUT':
      console.log('❌ LOGIN_FAIL/LOGOUT - clearing user and token');
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SET_LOADING':
      console.log('⏳ SET_LOADING:', action.payload);
      return {
        ...state,
        loading: action.payload,
      };
    case 'UPDATE_USER':
      console.log('👤 UPDATE_USER:', action.payload);
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user - wrapped in useCallback to prevent infinite loops
  const loadUser = useCallback(async () => {
    console.log('🔍 loadUser called with token:', state.token ? 'exists' : 'none');
    if (state.token) {
      try {
        console.log('📡 Making API call to /api/auth/me');
        const res = await api.get('/api/auth/me');
        console.log('✅ User loaded successfully:', res.data.data);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: res.data.data,
            token: state.token,
          },
        });
      } catch (err) {
        console.error('❌ Error loading user:', err);
        dispatch({ type: 'LOGIN_FAIL' });
      }
    } else {
      console.log('🚫 No token, setting loading to false');
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.token]);

  // Login user
  const login = async (email, password) => {
    console.log('🔐 Login attempt for:', email);
    try {
      console.log('📡 Making login request to:', api.defaults.baseURL + '/api/auth/login');
      const res = await api.post('/api/auth/login', { email, password });
      console.log('📡 Login response received:', res.status, res.data);
      
      const { token, user } = res.data;
      console.log('✅ Login successful, token received:', token ? 'yes' : 'no');
      
      localStorage.setItem('token', token);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });
      
      toast.success('Login successful!');
      return true;
    } catch (err) {
      console.error('❌ Login failed:', err);
      
      let message = 'Login failed';
      
      if (err.code === 'ECONNREFUSED') {
        message = 'Cannot connect to server. Please check if the backend is running.';
      } else if (err.code === 'ETIMEDOUT' || err.message.includes('timeout')) {
        message = 'Login request timed out. Please try again.';
      } else if (err.response?.status === 408) {
        message = 'Login request timed out. Please try again.';
      } else if (err.response?.status === 401) {
        message = 'Invalid email or password.';
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      } else if (err.message) {
        message = err.message;
      }
      
      console.error('📊 Error details:', {
        code: err.code,
        status: err.response?.status,
        message: err.message,
        response: err.response?.data
      });
      
      toast.error(message);
      dispatch({ type: 'LOGIN_FAIL' });
      return false;
    }
  };

  // Register user
  const register = async (userData) => {
    try {
      const res = await api.post('/api/auth/register', userData);
      const { token, user } = res.data;
      
      localStorage.setItem('token', token);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });
      
      toast.success('Registration successful!');
      return true;
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      toast.error(message);
      return false;
    }
  };

  // Logout user
  const logout = () => {
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const res = await api.put('/api/auth/updatedetails', userData);
      dispatch({
        type: 'UPDATE_USER',
        payload: res.data.data,
      });
      toast.success('Profile updated successfully!');
      return true;
    } catch (err) {
      const message = err.response?.data?.error || 'Update failed';
      toast.error(message);
      return false;
    }
  };

  // Update password
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      const res = await api.put('/api/auth/updatepassword', {
        currentPassword,
        newPassword,
      });
      const { token, user } = res.data;
      
      localStorage.setItem('token', token);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });
      
      toast.success('Password updated successfully!');
      return true;
    } catch (err) {
      const message = err.response?.data?.error || 'Password update failed';
      toast.error(message);
      return false;
    }
  };

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const value = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    login,
    register,
    logout,
    updateProfile,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 